/**
 * GA4 `sign_up` conversion measurement for committed OLM accounts.
 *
 * `pages/api/create-customer.js` answers 200 only after the authoritative WordPress
 * user exists and the WooCommerce customer it creates at `customers/${user.ID}`
 * exists too. Every other outcome — client validation, reCAPTCHA, a rejected or
 * already-used email, a WooCommerce failure — returns 400, which rejects the axios
 * call in `saveAndLoginUser` before this module is ever reached. Existing-user login
 * never passes through here at all; it goes to `pages/api/login.js`.
 *
 * So a 200 body carrying `user.ID` and a matching `customer.id` is the proof that one
 * account was committed, and that number is the internal account identifier
 * `helpers/checkout.js` already hands to WooCommerce as `order.customer_id`. Under the
 * current flow one committed account produces one such successful response.
 *
 * Nothing else from the response is ever sent: no email, phone, name, address,
 * form content, or URL.
 */

const SIGN_UP_EVENT = 'sign_up'

/** Approved `method` for an account created through OLM's own account API. */
const SIGN_UP_METHOD = 'olm_account'

/**
 * Both current paths create the self-serve storefront/customer account. Managed
 * service is a lead and CRM outcome that never runs through this helper.
 */
const PRODUCT_LINE = 'self_serve_platform'

/** The approved `signup_path` values, passed in by the caller that owns the journey. */
export const SIGNUP_PATH = {
    STANDALONE: 'standalone',
    CHECKOUT: 'checkout',
}

const APPROVED_SIGNUP_PATHS = new Set(Object.values(SIGNUP_PATH))

const emittedEventIds = new Set()

/** Canonical positive decimal integer: no sign, no leading zero, no separator. */
const CANONICAL_POSITIVE_INTEGER = /^[1-9][0-9]*$/

/**
 * The account number a value represents, or `null` unless it is a positive safe
 * integer, or a canonical positive decimal string for one.
 *
 * The type checks are deliberate rather than a bare `Number(value)`: coercion would
 * accept `true`, `[4101]`, `' 4101 '`, `'0x1005'`, `'4.1e3'` and `'04101'` as account
 * numbers and attribute a conversion to an identifier the backend never issued.
 */
const toAccountNumber = (value) => {
    if (typeof value === 'number') {
        return Number.isSafeInteger(value) && value > 0 ? value : null
    }
    if (typeof value !== 'string' || !CANONICAL_POSITIVE_INTEGER.test(value)) {
        return null
    }
    const id = Number(value)
    return Number.isSafeInteger(id) ? id : null
}

/**
 * The internal account identifier a `create-customer` 200 proves was committed,
 * or `null` when the response does not prove one whole account/customer pair.
 */
export const getCommittedAccountId = (accountData) => {
    const userId = toAccountNumber(accountData?.user?.ID)
    const customerId = toAccountNumber(accountData?.customer?.id)
    if (!userId || !customerId || userId !== customerId) return null
    return String(userId)
}

/**
 * The `event_id` for a committed account, or `null` when there is none.
 *
 * It is derived from the account identifier instead of being randomly generated, so
 * any measurement of the same account carries the same value rather than looking like
 * a separate conversion. That is a reporting aid only: GA4 does not deduplicate a
 * custom `event_id` parameter, so it supports downstream detection and backend
 * reconciliation but suppresses nothing by itself. It carries an internal number
 * only, never PII.
 */
export const getSignUpEventId = (accountData) => {
    const accountId = getCommittedAccountId(accountData)
    return accountId ? `olm_signup_${accountId}` : null
}

/**
 * `next.config.js` inlines Vercel's `VERCEL_ENV` into the client bundle as
 * `NEXT_PUBLIC_VERCEL_ENV`, and `helpers/shouldSkipLoginRecaptcha.js` already reads
 * those same values to tell preview and development apart from production. Emission
 * is therefore allow-listed to a Vercel production build: preview and `vercel dev`
 * report their own value, local `next dev` leaves it unset, and both stay silent.
 * No environment variable is added or changed.
 */
const isProductionMeasurementHost = () =>
    process.env.NEXT_PUBLIC_VERCEL_ENV === 'production'

/**
 * Emit one GA4 `sign_up` per committed account. Returns whether an event was sent.
 *
 * Sends nothing when the response does not prove a committed account, when the
 * journey is not an approved signup path, when this is not a production host, when
 * the Google tag is absent, or when this account already emitted from this loaded
 * module. A different committed account is never suppressed.
 *
 * That set is the module's own memory: it stops a repeat emission inside the active
 * page, and nothing more. It is discarded on reload and never existed on another
 * device, so it is not a claim about duplicates beyond this page context.
 */
export const trackAccountCreated = (accountData, signupPath) => {
    const eventId = getSignUpEventId(accountData)
    if (!eventId) return false
    if (!APPROVED_SIGNUP_PATHS.has(signupPath)) return false
    if (emittedEventIds.has(eventId)) return false
    if (!isProductionMeasurementHost()) return false
    if (typeof window === 'undefined') return false

    /* Measurement must never break account creation, so a missing or broken tag stays
       silent and leaves the id unmarked, still emittable later in this page. */
    try {
        if (typeof window.gtag !== 'function') return false
        window.gtag('event', SIGN_UP_EVENT, {
            method: SIGN_UP_METHOD,
            signup_path: signupPath,
            product_line: PRODUCT_LINE,
            event_id: eventId,
        })
    } catch {
        return false
    }

    emittedEventIds.add(eventId)
    return true
}
